import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import api from '../api';

export default function Home() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createName, setCreateName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    api.get('/leagues/mine').then(r => setLeagues(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createName.trim()) return;
    try {
      const res = await api.post('/leagues', { name: createName.trim() });
      setLeagues(prev => [{ ...res.data, member_count: 1, my_score: null }, ...prev]);
      setCreateName('');
      showMsg('success', `League "${res.data.name}" created! Invite code: ${res.data.invite_code}`);
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to create league.');
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    try {
      const res = await api.post('/leagues/join', { invite_code: joinCode.trim() });
      setLeagues(prev => [...prev, { ...res.data, member_count: (res.data.member_count || 0) + 1, my_score: null }]);
      setJoinCode('');
      showMsg('success', `Joined "${res.data.name}" successfully!`);
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to join league.');
    }
  };

  return (
    <div>
      <div className="home-header">
        <h1>WC2026 Bracket Challenge</h1>
        <p>Tournament starts June 11, 2026 · Pick your winners before it locks!</p>
        <p className="text-muted" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>This is a beta test game, fair warning - it may not work perfectly</p>
      </div>

      {msg.text && <div className={msg.type === 'error' ? 'error-msg mb-2' : 'success-msg mb-2'}>{msg.text}</div>}

      <div className="home-grid">
        {/* My Leagues */}
        <div className="card" style={{ gridColumn: 'span 2' }}>
          <h2>My Leagues</h2>
          {loading ? <div className="loading">Loading…</div> :
            leagues.length === 0 ? (
              <div className="empty-state">You're not in any leagues yet. Create one or ask a friend for their invite code!</div>
            ) : (
              <div className="league-list">
                {leagues.map(l => (
                  <div key={l.id} className="league-card">
                    <div>
                      <div className="league-card-name">{l.name}</div>
                      <div className="league-card-meta">
                        👥 {l.member_count} player{l.member_count !== 1 ? 's' : ''}
                        {l.submitted_brackets > 0 && ` · ${l.submitted_brackets} bracket${l.submitted_brackets !== 1 ? 's' : ''} submitted`}
                        {l.is_locked && <> · <span className="tag tag-locked">🔒 Locked</span></>}
                        {!l.is_locked && <> · <span className="tag tag-open">Open</span></>}
                      </div>
                      <div className="league-card-meta" style={{ marginTop: '0.25rem' }}>
                        Code: <span className="invite-code">{l.invite_code}</span>
                      </div>
                    </div>
                    <div className="league-card-actions">
                      {l.my_score != null && <span className="score-badge">{l.my_score} pts</span>}
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/league/${l.id}/bracket`)}>
                        {l.is_locked ? 'View Bracket' : 'Edit Bracket'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/league/${l.id}/leaderboard`)}>
                        Leaderboard
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          }
        </div>

        {/* Create */}
        <div className="card">
          <h2>Create a League</h2>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>League Name</label>
              <input value={createName} onChange={e => setCreateName(e.target.value)} placeholder="Office Pool 2026" maxLength={60} />
            </div>
            <button type="submit" className="btn btn-primary w-full" disabled={!createName.trim()}>Create League</button>
          </form>
          <p className="text-muted mt-2">An 8-character invite code will be generated for sharing with friends.</p>
        </div>

        {/* Join */}
        <div className="card">
          <h2>Join a League</h2>
          <form onSubmit={handleJoin}>
            <div className="form-group">
              <label>Invite Code</label>
              <input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())} placeholder="ABCD1234" maxLength={20} style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }} />
            </div>
            <button type="submit" className="btn btn-primary w-full" disabled={!joinCode.trim()}>Join League</button>
          </form>
          <p className="text-muted mt-2">Ask the league creator for their invite code.</p>
        </div>
      </div>

      {/* Scoring info */}
      <div className="card mt-3">
        <h2 style={{ marginBottom: '1rem' }}>How Scoring Works</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }}>
          {[
            ['Group Winner (1st)', '3 pts'],
            ['Group Runner-up (2nd)', '2 pts'],
            ['Group 3rd place', '1 pt'],
            ['3rd-place advances', '2 pts each'],
            ['Round of 32 win', '3 pts each'],
            ['Round of 16 win', '5 pts each'],
            ['Quarter-final win', '8 pts each'],
            ['Semi-final win', '11 pts each'],
            ['3rd Place Match', '5 pts'],
            ['Final correct winner', '15 pts'],
          ].map(([label, pts]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', background: 'var(--navy-light)', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem' }}>
              <span>{label}</span>
              <span style={{ fontWeight: 800, color: 'var(--gold)' }}>{pts}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
